import { extendTheme, type ThemeConfig, type StyleFunctionProps } from '@chakra-ui/react';
import { colors } from './assets/styles/colors';

const config: ThemeConfig = {
  initialColorMode: 'light',
  useSystemColorMode: true,
};

// Animation keyframes
const animations = {
  styles: {
    global: {
      '@keyframes spin': {
        '0%': {
          transform: 'rotate(0deg)',
        },
        '100%': {
          transform: 'rotate(360deg)',
        },
      },
      '@keyframes wiggle': {
        '0%, 100%': { transform: 'rotate(-3deg)' },
        '50%': { transform: 'rotate(3deg)' },
      },
      '@keyframes float': {
        '0%, 100%': { transform: 'translateY(0)' },
        '50%': { transform: 'translateY(-10px)' },
      },
      '@keyframes pulse': {
        '0%, 100%': { opacity: 1 },
        '50%': { opacity: 0.5 },
      },
      '@keyframes bounce': {
        '0%, 100%': { transform: 'scale(1)' },
        '50%': { transform: 'scale(1.1)' },
      },
    },
  },
};

const theme = extendTheme({
  config,
  styles: {
    global: (props: StyleFunctionProps) => ({
      body: {
        bg: props.colorMode === 'dark' ? colors.primary[800] : 'gray.50',
        color: props.colorMode === 'dark' ? 'white' : colors.primary[900],
        transition: 'background-color 0.2s ease-in-out, color 0.2s ease-in-out',
      },
      ...animations.styles.global,
    }),
  },
  colors: {
    brand: colors.primary,
    gradient: {
      purple: 'linear(to-l, #7928CA, #FF0080)',
      blue: 'linear(to-l, #0088FF, #4299E1)',
      green: 'linear(to-l, #56C288, #178656)',
      orange: 'linear(to-l, #FF8008, #FFA500)',
    },
  },
  semanticTokens: {
    colors: {
      'gradient-text': {
        default: 'linear(to-l, #7928CA, #FF0080)',
        _dark: 'linear(to-l, #9062EA, #FF369F)',
      },
    }
  },
  components: {
    Container: {
      baseStyle: {
        maxW: 'container.xl',
        px: { base: '4', md: '6' },
        py: { base: '6', md: '10' },
      },
    },
    Button: {
      defaultProps: {
        colorScheme: 'brand',
        size: 'md',
      },
      baseStyle: {
        borderRadius: 'lg',
        fontWeight: 'semibold',
        boxShadow: 'sm',
        transition: 'all 0.2s ease',
        _hover: {
          transform: 'translateY(-2px)',
          boxShadow: 'md',
        },
        _active: {
          transform: 'translateY(0)',
        },
      },
      variants: {
        solid: (props: StyleFunctionProps) => ({
          bg: props.colorMode === 'dark' ? colors.primary[500] : colors.primary[500],
          color: 'white',
          _hover: {
            bg: props.colorMode === 'dark' ? colors.primary[400] : colors.primary[600],
            transform: 'translateY(-2px)',
            boxShadow: 'md',
          },
        }),
        outline: (props: StyleFunctionProps) => ({
          borderColor: props.colorMode === 'dark' ? colors.primary[500] : colors.primary[500],
          color: props.colorMode === 'dark' ? colors.primary[300] : colors.primary[500],
          _hover: {
            bg: props.colorMode === 'dark' ? 'rgba(70, 68, 116, 0.2)' : 'rgba(70, 68, 116, 0.1)',
            transform: 'translateY(-2px)',
            boxShadow: 'md',
          },
        }),
        ghost: (props: StyleFunctionProps) => ({
          _hover: {
            bg: props.colorMode === 'dark' ? 'rgba(70, 68, 116, 0.2)' : 'rgba(70, 68, 116, 0.1)',
            transform: 'translateY(-2px)',
          },
        }),
        gradient: {
          bgGradient: 'linear(to-l, #7928CA, #FF0080)',
          color: 'white',
          _hover: {
            bgGradient: 'linear(to-l, #6520A8, #E5006E)',
            transform: 'translateY(-2px)',
            boxShadow: 'md',
          },
        },
      },
      sizes: {
        xl: {
          h: '16',
          minW: '16',
          fontSize: 'xl',
          px: '8',
        },
        lg: {
          h: '12',
          minW: '12',
          fontSize: 'lg',
          px: '6',
        },
        md: {
          h: '10',
          minW: '10',
          fontSize: 'md',
          px: '4',
        },
        sm: {
          h: '8',
          minW: '8',
          fontSize: 'sm',
          px: '3',
        },
        xs: {
          h: '6',
          minW: '6',
          fontSize: 'xs',
          px: '2',
        },
      },
    },
    Heading: {
      baseStyle: (props: StyleFunctionProps) => ({
        color: props.colorMode === 'dark' ? 'white' : colors.primary[700],
        fontWeight: 'bold',
        lineHeight: 'shorter',
      }),
      variants: {
        gradient: {
          bgGradient: 'var(--chakra-colors-gradient-text)',
          bgClip: 'text',
          fontWeight: 'extrabold',
        },
      },
    },
    Text: {
      baseStyle: (props: StyleFunctionProps) => ({
        color: props.colorMode === 'dark' ? 'gray.100' : colors.primary[800],
      }),
    },
    Card: {
      baseStyle: (props: StyleFunctionProps) => ({
        container: {
          backgroundColor: props.colorMode === 'dark' ? colors.primary[700] : 'white',
          boxShadow: 'lg',
          borderRadius: 'xl',
          overflow: 'hidden',
          transition: 'transform 0.2s ease, box-shadow 0.2s ease',
          _hover: {
            boxShadow: 'xl',
          },
          position: 'relative',
        },
        header: {
          paddingBottom: 0,
        },
        body: {
          padding: 6,
        },
      }),
    },
    Modal: {
      baseStyle: (props: StyleFunctionProps) => ({
        dialog: {
          borderRadius: 'xl',
          bg: props.colorMode === 'dark' ? colors.primary[700] : 'white',
          boxShadow: 'xl',
          overflow: 'hidden',
        },
        header: {
          fontSize: 'xl',
          fontWeight: 'bold',
          color: props.colorMode === 'dark' ? 'white' : colors.primary[700],
          bgGradient: props.colorMode === 'dark' ? 'none' : 'linear(to-l, #7928CA, #FF0080)',
          bgClip: props.colorMode === 'dark' ? 'none' : 'text',
        },
        body: {
          py: 6,
          px: 6,
          color: props.colorMode === 'dark' ? 'gray.100' : colors.primary[800],
        },
        footer: {
          py: 4,
          px: 6,
        },
        closeButton: {
          top: 4,
          right: 4,
        },
      }),
    },
    Tabs: {
      variants: {
        'soft-rounded': (props: StyleFunctionProps) => ({
          tab: {
            borderRadius: 'full',
            fontWeight: 'semibold',
            color: props.colorMode === 'dark' ? 'gray.400' : colors.primary[400],
            _selected: {
              color: 'white',
              bg: props.colorMode === 'dark' ? colors.primary[500] : colors.primary[500],
            },
            _hover: {
              color: props.colorMode === 'dark' ? 'white' : colors.primary[700],
              bg: props.colorMode === 'dark' ? 'rgba(70, 68, 116, 0.3)' : 'rgba(70, 68, 116, 0.1)',
            },
          },
          tablist: {
            gap: 2,
          },
        }),
      },
    },
    FormLabel: {
      baseStyle: (props: StyleFunctionProps) => ({
        color: props.colorMode === 'dark' ? 'gray.200' : colors.primary[700],
        fontWeight: 'semibold',
        mb: 2,
      }),
    },
    Input: {
      variants: {
        outline: (props: StyleFunctionProps) => ({
          field: {
            bg: props.colorMode === 'dark' ? colors.primary[700] : 'white',
            borderColor: props.colorMode === 'dark' ? colors.primary[600] : 'gray.200',
            borderRadius: 'md',
            transition: 'all 0.2s ease',
            _hover: {
              borderColor: props.colorMode === 'dark' ? colors.primary[500] : 'gray.300',
            },
            _focus: {
              borderColor: colors.primary[500],
              boxShadow: `0 0 0 1px var(--chakra-colors-brand-500)`,
            },
          },
        }),
      },
    },
    Select: {
      variants: {
        outline: (props: StyleFunctionProps) => ({
          field: {
            bg: props.colorMode === 'dark' ? colors.primary[700] : 'white',
            borderColor: props.colorMode === 'dark' ? colors.primary[600] : 'gray.200',
            borderRadius: 'md',
            transition: 'all 0.2s ease',
            _hover: {
              borderColor: props.colorMode === 'dark' ? colors.primary[500] : 'gray.300',
            },
            _focus: {
              borderColor: colors.primary[500],
              boxShadow: `0 0 0 1px var(--chakra-colors-brand-500)`,
            },
          },
        }),
      },
    },
    Badge: {
      baseStyle: {
        borderRadius: 'full',
        px: 3,
        py: 1,
        textTransform: 'capitalize',
        fontWeight: 'semibold',
      },
    },
    Stat: {
      baseStyle: (props: StyleFunctionProps) => ({
        container: {
          borderRadius: 'lg',
          p: 4,
          bg: props.colorMode === 'dark' ? colors.primary[600] : 'white',
          boxShadow: 'md',
          transition: 'transform 0.2s ease, box-shadow 0.2s ease',
          _hover: {
            transform: 'translateY(-2px)',
            boxShadow: 'lg',
          },
        },
        label: {
          fontWeight: 'semibold',
          fontSize: 'sm',
          color: props.colorMode === 'dark' ? 'gray.300' : 'gray.600',
        },
        number: {
          fontWeight: 'bold',
          fontSize: '2xl',
        },
      }),
    },
    Progress: {
      baseStyle: {
        track: {
          borderRadius: 'full',
        },
        filledTrack: {
          borderRadius: 'full',
        },
      },
    },
  },
});

export default theme;